import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMapLocationDot} from "@fortawesome/free-solid-svg-icons";
import React, {useEffect, useState} from "react";
import axios from "axios";
import {useDemandPoints} from "../../DemandPointContext";
import {calculateDistance} from "./support/DistanceCalculator";
import useGeolocation from "../../hooks/useGeolocation";
import {API_ROOT as API} from "../../config";

export const Dashboard = ({
  currentUserLocation,
  setCurrentUserLocation,
  onBuildingDetail
}) => {
  const {
    buildings,
    loading: buildingsLoading,
    error: buildingsError,
  } = useDemandPoints();

  const {
    location: userLocation,
    error: locationError,
    requestLocation
  } = useGeolocation();

  const [stats, setStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [dashboardSubTab, setDashboardSubTab] = useState('newest'); // 'newest' or 'nearby'

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data || {});
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({});
    } finally {
      setLoadingStats(false);
    }
  };

  const getNearbyBuildings = () => {
    if (!currentUserLocation) {
      return [];
    }

    return buildings.filter(building => {
      if (!building.location) {
        return false;
      }
      const distance = calculateDistance(
          currentUserLocation.latitude,
          currentUserLocation.longitude,
          building.location.latitude,
          building.location.longitude
      );
      return distance <= 1000; // 1km radius for nearby buildings
    }).slice(0, 5); // Limit to 5 buildings
  };

  const handleBuildingDetailsClick = (building) => {
    onBuildingDetail(building);
  };

  return (
      <div className="container mx-auto p-4">
        <h2 className="text-2xl font-bold mb-6">Dashboard</h2>

        {userLocation && (
            <div
                className="bg-green-100 border border-green-300 rounded p-4 mb-6">
              <p className="text-green-800">
                <FontAwesomeIcon icon={faMapLocationDot} className="mr-2"/>
                Aktuelle Position: {userLocation.latitude.toFixed(
                  6)}, {userLocation.longitude.toFixed(6)}
                {userLocation.accuracy && ` (±${Math.round(
                    userLocation.accuracy)}m)`}
              </p>
            </div>
        )}

        {locationError && (
            <div className="bg-red-100 border border-red-300 rounded p-4 mb-6">
              <p className="text-red-800">⚠️ GPS-Fehler: {locationError}</p>
            </div>
        )}

        {buildingsError && (
            <div className="bg-red-100 border border-red-300 rounded p-4 mb-6">
              <p className="text-red-800">⚠️ {buildingsError}</p>
            </div>
        )}

        {loadingStats ? (
            <div className="flex justify-center items-center h-32">
              <div className="loading-spinner"></div>
              <span className="ml-2">Lade Daten...</span>
            </div>
        ) : (
            <>
              <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="stats-card stats-card-buildings">
                  <h3 className="stats-label">Gebäude gesamt</h3>
                  <p className="stats-number">{stats.totalBuildings || 0}</p>
                </div>
                <div className="stats-card stats-card-photos">
                  <h3 className="stats-label">Fotos gesamt</h3>
                  <p className="stats-number">{stats.totalPhotos || 0}</p>
                </div>
                <div className="stats-card stats-card-scans">
                  <h3 className="stats-label">Scans gesamt</h3>
                  <p className="stats-number">{stats.totalScans || 0}</p>
                </div>
                <div className="stats-card stats-card-documents">
                  <h3 className="stats-label">Dokumente</h3>
                  <p className="stats-number">{stats.totalDocuments || 0}</p>
                </div>
              </div>

              {/* Dashboard SubTabs */}
              {buildingsLoading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="loading-spinner"></div>
                    <span className="ml-2">Lade Daten...</span>
                  </div>
              ) : (
                  <div className="card">
                    <div className="card-header border-b-0">
                      {/* Tab Navigation - same style as Gebäude tabs */}
                      <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                          <button
                              onClick={(e) => {
                                e.preventDefault();
                                setDashboardSubTab('newest');
                              }}
                              onTouchStart={(e) => e.preventDefault()}
                              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                  dashboardSubTab === 'newest'
                                      ? 'border-orange-500 text-orange-600'
                                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                              }`}
                          >
                            Neueste Gebäude
                          </button>
                          <button
                              onClick={async (e) => {
                                e.preventDefault();
                                if (!currentUserLocation) {
                                  try {
                                    const location = await requestLocation();
                                    setCurrentUserLocation(location);
                                  } catch (error) {
                                    alert(`GPS-Fehler: ${error}`);
                                    return;
                                  }
                                }
                                setDashboardSubTab('nearby');
                              }}
                              onTouchStart={(e) => e.preventDefault()}
                              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                  dashboardSubTab === 'nearby'
                                      ? 'border-orange-500 text-orange-600'
                                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                              }`}
                          >
                            Umgebung
                            {dashboardSubTab === 'nearby'
                                && !currentUserLocation
                                && (
                                    <span
                                        className="text-xs text-orange-500 ml-1">(GPS erforderlich)</span>
                                )}
                          </button>
                        </nav>
                      </div>
                    </div>

                    {dashboardSubTab === 'newest' ? (
                        // Newest Buildings Tab Content
                        buildings.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">
                              Noch keine Gebäude erfasst. <br/>
                              <button
                                  disabled={true}
                                  onClick={() => switchToView('building-form')}
                                  className="btn-primary mt-2"
                              >
                                Erstes Gebäude erfassen
                              </button>
                            </p>
                        ) : (
                            buildings.slice(0, 5).map((building) => (
                                <div key={building.id}
                                     className="border-b border-gray-200 py-4 px-2 last:border-b-0">
                                  <div
                                      className="flex justify-between items-center">
                                    <div className="flex-1 pr-4">
                                      <p className="font-semibold mb-1">
                                        {building.address?.street} {building.address?.house_number
                                          ?? ''}{building.address?.house_number_suffix
                                          || ''}
                                      </p>
                                      <p className="text-sm text-gray-600 mb-2">
                                        {building.address?.zip_code} {building.address?.city}
                                        {building.address?.district && <span
                                            className="text-gray-500"> ({building.address.district})</span>}
                                      </p>
                                      <p className="text-xs text-gray-500 mb-2">
                                        {building.total_floors || 0} Ebenen
                                        • {building.residential_units || 0} WE
                                        • {building.business_units || 0} GE
                                      </p>
                                      {building.created_at && (
                                          <p className="text-xs text-gray-400 mt-2">
                                        <span
                                            className="font-medium">Erstanlage:</span> {new Date(
                                              building.created_at).toLocaleDateString(
                                              'de-DE')}
                                            {building.updated_at
                                                && building.updated_at
                                                !== building.created_at && (
                                                    <span className="ml-4">
                                <span
                                    className="font-medium">Letzte Änderung:</span> {new Date(
                                                        building.updated_at).toLocaleDateString(
                                                        'de-DE')}
                              </span>
                                                )}
                                          </p>
                                      )}
                                    </div>
                                    <button
                                        onClick={() => onBuildingDetail(
                                            building)}
                                        className="btn-details px-4 py-2 min-w-[80px]"
                                    >
                                      Details
                                    </button>
                                  </div>
                                </div>
                            ))
                        )
                    ) : (
                        // Nearby Buildings Tab Content
                        (() => {
                          const nearbyBuildings = getNearbyBuildings();
                          return nearbyBuildings.length === 0 ? (
                              <div className="text-center py-8">
                                {!currentUserLocation ? (
                                    <p className="text-gray-500">
                                      GPS-Position erforderlich für
                                      Umgebungssuche
                                    </p>
                                ) : (
                                    <p className="text-gray-500">
                                      Keine Gebäude in der Umgebung gefunden
                                      (1km
                                      Radius)
                                    </p>
                                )}
                              </div>
                          ) : (
                              nearbyBuildings.map((building) => {
                                const distance = currentUserLocation
                                    ? calculateDistance(
                                        currentUserLocation.latitude,
                                        currentUserLocation.longitude,
                                        building.location.latitude,
                                        building.location.longitude
                                    ) : 0;

                                return (
                                    <div key={building.id}
                                         className="border-b border-gray-200 py-3 last:border-b-0">
                                      <div
                                          className="flex justify-between items-center">
                                        <div>
                                          <p className="font-semibold">
                                            {building.address?.street} {building.address?.house_number
                                              ?? ''}{building.address?.house_number_suffix
                                              || ''}
                                          </p>
                                          <p className="text-sm text-gray-600">
                                            {building.address?.zip_code} {building.address?.city}
                                            {building.address?.district && <span
                                                className="text-gray-500"> ({building.address.district})</span>}
                                          </p>
                                          <p className="text-xs text-orange-600 mt-1">
                                            📍 {Math.round(distance)}m entfernt
                                          </p>
                                        </div>
                                        <button
                                            onClick={() => handleBuildingDetailsClick(
                                                building)}
                                            className="btn-details"
                                        >
                                          Details
                                        </button>
                                      </div>
                                    </div>
                                );
                              })
                          );
                        })()
                    )}
                  </div>)}
            </>
        )}
      </div>
  );
};